// src/config/themes.ts
import { createTheme, ThemeOptions } from '@mui/material/styles';

// Brand accent colors injected from brand config — override palette defaults below
import { brand as _brand } from './brand';
const _brandAccent = {
  fire: _brand.colors.fire,
  fireSecondary: _brand.colors.fireSecondary,
  fireLight: _brand.colors.fireLight,
  fireDark: _brand.colors.fireDark,
};

// Base color palettes for different themes
export const colorPalettes = {
  dark: {
    background: {
      primary: '#0d1117',      // Very dark gray/black
      secondary: '#161b22',    // Dark gray cards
      tertiary: '#21262d',     // Lighter gray surfaces
      paper: '#0d1117',        // Paper background
    },
    text: {
      primary: '#f0f6fc',      // Pure white text
      secondary: '#8b949e',    // Gray text
      disabled: '#484f58',     // Disabled text
    },
    accent: _brandAccent,
    border: '#30363d',         // Border color
    divider: '#21262d',        // Divider color
  },
  light: {
    background: {
      primary: '#ffffff',
      secondary: '#f6f8fa',
      tertiary: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#24292f',
      secondary: '#656d76',
      disabled: '#8c959f',
    },
    accent: _brandAccent,
    border: '#d0d7de',
    divider: '#d8dee4',
  },
  midnight: {
    // Deep indigo/violet base — distinct from dark's neutral gray
    background: {
      primary: '#0c0a1a',      // deep indigo-black
      secondary: '#16132e',    // dark violet cards
      tertiary: '#211d42',     // lighter violet surfaces
      paper: '#0c0a1a',
    },
    text: {
      primary: '#e8e4f8',      // soft lavender white
      secondary: '#9b93c0',    // muted violet-gray
      disabled: '#5c5580',
    },
    accent: {
      fire: '#a855f7',         // vivid purple
      fireSecondary: '#c084fc',
      fireLight: '#d8b4fe',
      fireDark: '#7c3aed',
    },
    border: '#2e2854',
    divider: '#1e1a3a',
  },
  ocean: {
    // Deep teal/cyan marine base — distinctly blue-green
    background: {
      primary: '#071a1f',      // deep marine
      secondary: '#0d2a33',    // dark teal cards
      tertiary: '#143d4a',     // ocean surface
      paper: '#071a1f',
    },
    text: {
      primary: '#e0f4f8',      // icy white
      secondary: '#7cb8c9',    // muted teal
      disabled: '#4a8494',
    },
    accent: {
      fire: '#06b6d4',         // vivid cyan
      fireSecondary: '#22d3ee',
      fireLight: '#67e8f9',
      fireDark: '#0891b2',
    },
    border: '#1a4a58',
    divider: '#0f3540',
  },
};

import { brand } from './brand';

// Logo colors — from brand config
export const logoColors = brand.logoColors;

// Theme creator function
export const createBrandTheme = (themeName: keyof typeof colorPalettes): any => {
  const palette = colorPalettes[themeName];
  
  const themeOptions: ThemeOptions = {
    palette: {
      mode: themeName === 'light' ? 'light' : 'dark',
      primary: {
        main: palette.accent.fire,
        light: palette.accent.fireLight,
        dark: palette.accent.fireDark,
        contrastText: palette.text.primary,
      },
      secondary: {
        main: palette.accent.fireSecondary,
        light: palette.accent.fireLight,
        dark: palette.accent.fire,
        contrastText: palette.text.primary,
      },
      background: {
        default: palette.background.primary,
        paper: palette.background.secondary,
      },
      text: {
        primary: palette.text.primary,
        secondary: palette.text.secondary,
        disabled: palette.text.disabled,
      },
      divider: palette.divider,
      success: {
        main: '#22c55e',
        light: '#4ade80',
        dark: '#16a34a',
      },
      warning: {
        main: palette.accent.fireSecondary,
        light: palette.accent.fireLight,
        dark: palette.accent.fire,
      },
      error: {
        main: '#ef4444',
        light: '#f87171',
        dark: '#dc2626',
      },
      info: {
        main: logoColors.teal,
        light: '#34d399',
        dark: '#059669',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "SF Pro Display", system-ui, sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
        color: palette.text.primary,
      },
      h2: {
        fontWeight: 600,
        fontSize: '2rem',
        lineHeight: 1.3,
        color: palette.text.primary,
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
        lineHeight: 1.4,
        color: palette.text.primary,
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
        color: palette.text.primary,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.5,
        color: palette.text.primary,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.5,
        color: palette.text.primary,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 20px',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${palette.accent.fire}40`,
            },
          },
          contained: {
            backgroundColor: palette.accent.fire,
            color: palette.text.primary,
            '&:hover': {
              backgroundColor: palette.accent.fireDark,
            },
          },
          outlined: {
            borderColor: palette.accent.fire,
            color: palette.accent.fire,
            '&:hover': {
              borderColor: palette.accent.fireDark,
              backgroundColor: `${palette.accent.fire}10`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.secondary,
            border: `1px solid ${palette.border}`,
            color: palette.text.primary,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 25px ${palette.background.tertiary}80`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.secondary,
            backgroundImage: 'none',
            color: palette.text.primary,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.secondary,
            borderBottom: `1px solid ${palette.border}`,
            boxShadow: 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.border,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.accent.fire,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.accent.fire,
              },
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.tertiary,
            '& .MuiTableCell-head': {
              color: palette.text.primary,
              fontWeight: 600,
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.tertiary,
          },
          bar: {
            backgroundColor: palette.accent.fire,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
          colorPrimary: {
            backgroundColor: palette.accent.fire,
            color: palette.text.primary,
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

// Theme names for UI selector
export const themeNames = {
  dark: 'Dark',
  light: 'Light',
  midnight: 'Midnight',
  ocean: 'Ocean',
} as const;

export type ThemeName = keyof typeof themeNames;

// Backward compat alias
export const createLoxiaTheme = createBrandTheme;
